import { create } from "zustand";

/**
 * Громкость чужого звука: голоса и трансляции по отдельности.
 *
 * Нужна потому, что уровень приходит таким, каким его отдал источник. У
 * показывающего экран игра может быть убавлена почти в ноль — так бывает у
 * тех, чьи наушники сами по себе громкие, — и зрителю нужно прибавить, а не
 * просить того перенастроить свой компьютер.
 *
 * Отсюда и потолок выше ста процентов: вернуть убавленное можно только
 * усилением. Обычный звуковой элемент этого не умеет — его громкость
 * ограничена единицей, — поэтому звук идёт через Web Audio с узлом усиления.
 */

/** Во сколько раз можно прибавить. Три — это плюс десять децибел. */
export const MAX_VOLUME = 3;

/**
 * Общая громкость: то, что в настройках зовётся «Громкость звука».
 *
 * Живёт в том же хранилище отдельным ключом, а не рядом с ним: правила у неё
 * те же — те же ноль, та же сотня без усиления, тот же потолок, — и вторая
 * такая же машинерия рядом была бы копией.
 */
export const MASTER_KEY = "master";

const KEY = "airel.volume";

type Store = Record<string, number>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

/**
 * Громкости — стором zustand, а не своим списком подписчиков.
 *
 * Состояние тут по-прежнему модульное: звук вставлен в дерево отдельно от
 * того, где стоят ползунки, и таскать значение между ними было бы длиннее,
 * чем оповестить. Меняется только способ оповещения.
 *
 * Своих таких списков было пять — здесь, в звуке трансляции, настройке
 * микрофона, выборе устройств и шумодаве, — и они успели разойтись. Между
 * первой отрисовкой и подпиской в эффекте значение может смениться, и
 * подписавшийся об этом уже не узнает: двое из пяти перечитывали его сразу
 * после `add`, трое нет. `useSyncExternalStore` под zustand подписывается
 * до отрисовки, и вопроса не остаётся вовсе.
 *
 * Наружу видны две стороны: хук для отрисовки и `getState()` для тех, кто
 * зовёт не из React, — а зовут отсюда чаще, чем рисуют.
 */
export const useVolumes = create<Store>()(() => read());

/**
 * Ключ громкости.
 *
 * Голос и трансляция одного человека настраиваются порознь: игру часто хочется
 * тише разговора или наоборот.
 */
export function volumeKey(owner: string, screen: boolean): string {
  return screen ? `${owner}:screen` : owner;
}

export function volumeOf(key: string): number {
  const value = useVolumes.getState()[key];
  return typeof value === "number" && value >= 0 && value <= MAX_VOLUME ? value : 1;
}

export function setVolume(key: string, value: number) {
  useVolumes.setState({ [key]: value });

  try {
    localStorage.setItem(KEY, JSON.stringify(useVolumes.getState()));
  } catch {
    // Переполненное хранилище — не повод терять звук: громкость доживёт до
    // конца сеанса в памяти.
  }
}

/**
 * Заглушить для себя и вернуть как было.
 *
 * Заглушение — это ноль на той же шкале, а не отдельный признак: иначе
 * получаются два выключателя на один звук, и «заглушён, но громкость 185 %»
 * приходится как-то объяснять. Прежнее значение откладывается рядом, чтобы
 * возврат не сбрасывал настроенное в сто процентов.
 */
const WAS_KEY = "airel.volume-was";

function readWas(): Store {
  try {
    const raw = localStorage.getItem(WAS_KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

let was = readWas();

export function toggleMute(key: string) {
  const value = volumeOf(key);

  if (value > 0) {
    was = { ...was, [key]: value };
    try {
      localStorage.setItem(WAS_KEY, JSON.stringify(was));
    } catch {
      // Не сохранилось — вернём сто процентов. Хуже, но не молча тихо.
    }
    setVolume(key, 0);
    return;
  }

  setVolume(key, was[key] ?? 1);
}

/**
 * Общий звуковой узел.
 *
 * Один на всё приложение: каждый новый стоит системе отдельного потока, а
 * заводить их по одному на собеседника незачем.
 *
 * Создаётся при первом звуке, а не заранее: до действия человека браузер
 * всё равно держал бы его в приостановленном состоянии.
 */
let shared: AudioContext | null = null;

export function audioContext(): AudioContext {
  shared ??= new AudioContext();

  // Телефоны приостанавливают узел, пока не будет касания. Попытка возобновить
  // ничего не стоит и молча ничего не делает, если он и так работает.
  void shared.resume().catch(() => {});

  return shared;
}

let master: GainNode | null = null;
let tap: AnalyserNode | null = null;

/**
 * Общий узел вывода: через него идёт весь чужой звук.
 *
 * Нужен ради двух вещей сразу — общей громкости и полоски уровня в
 * настройках. Поканальные усилители для этого не годятся: они разные для
 * каждого собеседника, а «сколько сейчас слышно» — величина одна.
 */
export function outputNode(): GainNode {
  if (master) return master;

  const context = audioContext();
  master = context.createGain();
  master.gain.value = volumeOf(MASTER_KEY);

  tap = context.createAnalyser();
  tap.fftSize = 1024;

  master.connect(tap);
  master.connect(context.destination);

  /*
   * Общая громкость доезжает до узла подпиской, а не отрисовкой: узел живёт
   * вне дерева React, и рисовать ради него нечего. Отписки нет намеренно —
   * узел один на всё приложение и не умирает раньше вкладки.
   */
  useVolumes.subscribe((next) => {
    const value = next[MASTER_KEY];
    if (master) master.gain.value = typeof value === "number" ? value : 1;
  });

  return master;
}

/** Куда смотреть, чтобы нарисовать уровень выхода. */
export function outputTap(): AnalyserNode {
  outputNode();
  return tap!;
}
